import { useEffect, useState } from 'react';

import { useReplyDraftsGateway } from './ReplyDraftsGatewayProvider';
import { getReplyDraftsV2QueryOptions } from '../api/queryOptions';
import { findOfferedDraft } from '../model/status';

import { useUserScopedQuery } from '@/hooks/useUserScopedQuery';

/**
 * The draft `.draft` opens in a conversation, resolved from the expert's open
 * drafts. The shortcut is offered before that list is known, so the surface it
 * opens asks here and shows what it finds: loading, a draft, none, or a failure.
 *
 * The list is refetched on every open, not read from a cache up to a minute
 * old: a draft that turned ready since the chat loaded would otherwise read as
 * none. While that request runs the surface keeps loading rather than showing
 * a stale answer, and a retry shows loading too. Until there is an expert to
 * fetch for, the scoped query reads as loading as well.
 *
 * The first draft found is kept for the conversation while the surface stays
 * mounted. The list is refetched after sends, and a draft that leaves it
 * mid-edit must not swap the wizard out from under the expert.
 */
export function useOfferedReplyDraft(conversationId: string) {
  const query = useUserScopedQuery(getReplyDraftsV2QueryOptions, [useReplyDraftsGateway()], {
    refetchOnMount: 'always',
  });

  const [pinned, setPinned] = useState<{ conversationId: string; id: string } | null>(null);

  const found = findOfferedDraft(query.data ?? [], conversationId)?.id ?? null;

  const pinnedId = pinned?.conversationId === conversationId ? pinned.id : null;

  useEffect(() => {
    if (!pinnedId && found) {
      setPinned({ conversationId, id: found });
    }
  }, [pinnedId, found, conversationId]);

  const replyDraftId = pinnedId ?? found;

  const isLoading = !replyDraftId && (query.isLoading || query.isFetching);

  return {
    replyDraftId,
    isLoading,
    isError: !replyDraftId && !isLoading && query.isError,
    refetch: () => {
      query.refetch().catch(() => undefined);
    },
  };
}
